import 'dayjs/plugin/timezone'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDoc, EventDoc, GroupDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'

import { highestActiveTier } from './api/accounts'
import {
  isGroupOutOfTier,
  isUserActiveInGroup,
  isUserCanOperateEvents
} from './api/groups'
import { isEventRecurrenceInstanceValid } from './api/instance_state'
import { isConvertedGroupId } from './groups'

dayjs.extend(timezone)
export const eventIdFormat = 'YYYY-MM-DD-HH-mm'

export function isEventEditable(
  event: EventDoc | null | undefined,
  groups: Model<GroupDoc>[] | GroupDoc[],
  account: Model<AccountDoc> | AccountDoc
) {
  if (!event) return false

  if (isEventBooked(event)) return false

  const highestTier = highestActiveTier(account)

  if (isConvertedGroupId(event.GroupID)) return true

  const toCheckIds = [event.GroupID, ...(event.SupplementaryGroupsIDs ?? [])]

  for (const groupId of toCheckIds) {
    if (!groupId) continue

    const group = groups?.find((g) => g._id === groupId)

    // if group not found, immediately return false
    if (!group) return false

    // if user not active in any of groups, return false as well
    if (
      !isUserActiveInGroup(
        group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )
    )
      return false

    var consolidatedState =
      (event.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID) &&
        group?.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents ===
          '1') ||
      isUserCanOperateEvents(
        group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )

    if (!consolidatedState) return false

    if (
      consolidatedState &&
      isGroupOutOfTier(highestTier, group, account) &&
      group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
    ) {
      return false
    }
  }

  return true
}

export function getSupportedTimezone(tz: string | undefined | null): string {
  if (!tz || [':', '-'].some((char) => tz.includes(char))) {
    // Fallback: try to guess or use UTC
    return dayjs.tz.guess()
  }

  return tz
}
export const bookedEvent = 'BOOKED_SLOT'
export function isEventRecurrenceValid(
  event: EventDoc,
  visibleAreaStart: dayjs.Dayjs
) {
  return isEventRecurrenceInstanceValid(event, visibleAreaStart)
}

export function isEventBooked(event: EventDoc) {
  return event.Text === bookedEvent
}

export function provideEventTitle(event: EventDoc) {
  if (isEventBooked(event)) return t('bookedSlot')

  return event.Text ?? ''
}

export function getPendingEventData() {
  const storedData = LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA)
  let pendingData: EventDoc | undefined = undefined
  if (storedData) {
    pendingData = JSON.parse(storedData)
  }
  return pendingData
}

export function eventIdForContainer(
  event: EventDoc | undefined,
  instanceStart: number
) {
  if (!event) return instanceStart.toString()

  try {
    return event.AllDay === '1'
      ? `${event?.Text}-${dayjs(instanceStart).hour(12).format(eventIdFormat)}`
      : `${event?.Text}-${dayjs(instanceStart)
          .tz(
            (event.TimeZoneNameID?.length ?? 0) > 0
              ? event.TimeZoneNameID
              : getDefaultTimezone()
          )
          .format(eventIdFormat)}`
  } catch (error) {
    return event.AllDay === '1'
      ? `${event?.Text}-${dayjs(instanceStart).hour(12).format(eventIdFormat)}`
      : `${event?.Text}-${dayjs(instanceStart).format(eventIdFormat)}`
  }
}

export function eventLengthInDays(event?: EventDoc) {
  if (!event) return 0
  if (!event.StartDate || !event.EndDate) return 0

  // Convert Unix timestamps to milliseconds
  const startTimestamp = Number(event.StartDate) * 1000
  const endTimestamp = Number(event.EndDate) * 1000

  // Create normalized dates (set to midnight)
  const startDateDay = new Date(startTimestamp)
  startDateDay.setHours(0, 0, 0, 0)

  const endDateDay = new Date(endTimestamp)
  endDateDay.setHours(0, 0, 0, 0)

  // Calculate difference in days
  return (
    Math.round(
      (endDateDay.getTime() - startDateDay.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1
  )
}

export function isEventMoreThanADay(event: EventDoc) {
  return eventLengthInDays(event) > 1
}
